import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import leadsService from '../../services/leads';
import leadPurchasesService from '../../services/leadPurchases';
import { Card, Form, Input, Button, Typography, Row, Col, Spin, message, Grid } from 'antd';

const { useBreakpoint } = Grid;

const LeadDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentUser } = useSelector((state) => state.user);
  const isAdmin = currentUser?.role === 'admin';
  const [form] = Form.useForm();
  const screens = useBreakpoint();

  const { data: lead, isLoading, error } = useQuery({
    queryKey: ['lead', id],
    queryFn: () => leadsService.getLead(id),
    retry: false
  });

  useEffect(() => {
    if (error?.response?.status === 403) {
      //message.error('You are not authorized to view this lead');
      navigate('/leads');
    } else if (error?.response?.status === 404) {
      //message.error('Lead not found');
      navigate('/leads');
    }
  }, [error, navigate]);

  const updateMutation = useMutation({
    mutationFn: (updatedData) => leadsService.updateLead(id, updatedData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
      message.success('Lead updated successfully');
    },
    onError: (error) => {
      if (error.response?.status === 404) {
        message.error('Lead not found');
        navigate('/leads');
      } else {
        message.error('Failed to update lead: ' + error.message);
      }
    }
  });

  const purchaseMutation = useMutation({
    mutationFn: () => leadPurchasesService.purchaseLead(id),
    onSuccess: () => {
      message.success('Lead purchased successfully!');
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
    },
    onError: (error) => {
      if (error.response?.status === 404) {
        message.error('Lead not found');
        navigate('/leads');
      } else {
        message.error('Failed to purchase lead: ' + error.message);
      }
    }
  });

  const handleUpdate = (values) => {
    updateMutation.mutate(values);
  };

  const handlePurchase = () => {
    if (lead.purchaser && lead.purchaser.id === currentUser.id) {
      message.info('You have already purchased this lead. You can view it in your Purchases tab.');
      return;
    }

    purchaseMutation.mutate();
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    if (error.response?.status === 403 || error.response?.status === 404) {
      return null; // Will be handled by useEffect
    }
    return <Typography.Text type="danger">Error loading lead: {error.message}</Typography.Text>;
  }

  return (
    <div style={{ 
      padding: screens.xs ? '12px' : '24px',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
      minHeight: '100vh'
    }}>
      <Typography.Title level={2} style={{ marginBottom: 24 }}>
        Lead Details
      </Typography.Title>

      <Card>
        <Form
          form={form}
          layout="vertical"
          initialValues={lead}
          onFinish={handleUpdate}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: 'Please input the name!' }]}
              >
                <Input disabled={!isAdmin} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="industry_type"
                label="Industry Type"
                rules={[{ required: true, message: 'Please input the industry type!' }]}
              >
                <Input disabled={!isAdmin} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="state"
                label="State"
                rules={[{ required: true, message: 'Please input the state!' }]}
              >
                <Input disabled={!isAdmin} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="price"
                label="Price"
                rules={[{ required: true, message: 'Please input the price!' }]}
              >
                <Input disabled={!isAdmin} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name="description"
                label="Description"
              >
                <Input.TextArea disabled={!isAdmin} />
              </Form.Item>
            </Col>
          </Row>
          {(isAdmin || lead?.contact_information) && (
            <Row gutter={16}>
              <Col xs={24}>
                <Typography.Title level={4}>Contact Information</Typography.Title>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name={['contact_information', 'email']}
                  label="Email"
                  rules={[
                    { required: true, message: 'Please input the email!' },
                    { type: 'email', message: 'Please enter a valid email!' }
                  ]}
                >
                  <Input disabled={!isAdmin} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name={['contact_information', 'phone']}
                  label="Phone"
                  rules={[{ required: true, message: 'Please input the phone number!' }]}
                >
                  <Input disabled={!isAdmin} />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name={['contact_information', 'address']}
                  label="Address"
                >
                  <Input.TextArea disabled={!isAdmin} />
                </Form.Item>
              </Col>
            </Row>
          )}

          {(isAdmin || lead?.additional_details) && (
            <Row gutter={16}>
              <Col xs={24}>
                <Typography.Title level={4}>Additional Details</Typography.Title>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name={['additional_details', 'company_size']}
                  label="Company Size"
                >
                  <Input disabled={!isAdmin} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name={['additional_details', 'annual_revenue']}
                  label="Annual Revenue"
                >
                  <Input disabled={!isAdmin} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name={['additional_details', 'interest_level']}
                  label="Interest Level"
                >
                  <Input disabled={!isAdmin} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name={['additional_details', 'source']}
                  label="Source"
                >
                  <Input disabled={!isAdmin} />
                </Form.Item>
              </Col>
            </Row>
          )}

          <div style={{ marginTop: 24 }}>
            {isAdmin && (
              <Button
                type="primary"
                htmlType="submit"
                loading={updateMutation.isLoading}
              >
                Update Lead
              </Button>
            )}
            
            {!isAdmin && lead?.availability_status && !lead?.contact_information && (
              <Button
                type="primary"
                onClick={handlePurchase}
                loading={purchaseMutation.isLoading}
              >
                Purchase Lead
              </Button>
            )}
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default LeadDetailPage; 